import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import Page from 'flow/components/Setting/Page';
import Button from 'flow/components/ui-components/Button';
import TextArea from 'flow/components/ui-components/TextArea';
import Loading from 'flow/components/ui-components/Loading';
import useAppContext from 'flow/hooks/useAppContext';
import { setSnackbar } from 'flow/actions/app-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    useAutoReplyTemplates,
    useCreateAutoReplyTemplate,
    useDeleteAutoReplyTemplate,
    useUpdateAutoReplyTemplate,
} from 'flow/hooks/api/useAutoReplyTemplates';
import {
    selectQueueEditable,
    selectQueueProfile,
} from 'flow/selectors/queue-selectors';
import { ProfileKey, QueueKey } from 'flow/types/EntityKeys';
import { AutoReplyTemplateMessageType } from 'flow/types/AutoReplyTemplate';

const MAX_CHAR_LIMIT = 1600;

type FormProps = {
    initialValue: string;
    disabled: boolean;
    onSave: (input: string) => void;
};

const AutoReplyMessageForm = ({
    initialValue,
    disabled,
    onSave,
}: FormProps) => {
    const { i18next } = useAppContext();
    const [input, setInput] = useState(initialValue);
    const remainingChars = MAX_CHAR_LIMIT - input.length;

    const handleInputChange = (event: any) => {
        const newValue = event.target.value;
        if (newValue.length <= MAX_CHAR_LIMIT) {
            setInput(newValue);
        }
    };

    return (
        <div className="flex flex-col gap-8">
            <div className="text-sm text-gray-500">
                {i18next.t('tickets.autoreply.instruction')}
            </div>
            <div>
                <TextArea
                    value={input}
                    onChange={handleInputChange}
                    disabled={disabled}
                    rows={12}
                />
                <div className="mt-2 text-sm text-gray-500">
                    <span>
                        {i18next.t('Channel.Settings.Greeting.CharacterLimit', {
                            characters: remainingChars,
                        })}
                    </span>
                    {remainingChars === 0 ? (
                        <span className="ml-2 text-red-500">
                            {i18next.t(
                                'Channel.Settings.Greeting.CharacterLimitReached',
                            )}
                        </span>
                    ) : null}
                </div>
            </div>
            <div className="flex justify-end">
                <Button
                    color="gray"
                    className="hover:bg-main hover:darken-0 min-w-max px-5 hover:text-white"
                    onClick={() => onSave(input)}
                    disabled={disabled}
                >
                    {i18next.t('navigate.save')}
                </Button>
            </div>
        </div>
    );
};

const AutoReplyMessage = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { entityKey, profileKey, messageType } = useParams<{
        entityKey: QueueKey;
        profileKey: ProfileKey;
        messageType: AutoReplyTemplateMessageType;
    }>();

    const profile = useAppSelector((state) =>
        selectQueueProfile(state, entityKey, profileKey),
    );

    const queueEditable = useAppSelector((state) =>
        selectQueueEditable(state, entityKey),
    );

    const { data: templates, isLoading } = useAutoReplyTemplates(entityKey);
    const createTemplate = useCreateAutoReplyTemplate(entityKey);
    const updateTemplate = useUpdateAutoReplyTemplate(entityKey);
    const deleteTemplate = useDeleteAutoReplyTemplate(entityKey);

    const template = templates?.find(
        (t) =>
            t.profileEntityKey === profileKey && t.messageType === messageType,
    );

    if (!profile || isLoading) {
        return <Loading />;
    }

    const onSaveSuccess = () => {
        dispatch(
            setSnackbar({ i18nextMessage: 'Settings.Update.Snackbar.Success' }),
        );
        history.goBack();
    };

    const onSaveError = () => {
        dispatch(
            setSnackbar({ i18nextMessage: 'Settings.Update.Snackbar.Fail' }),
        );
    };

    const handleSave = (input: string) => {
        const isEmpty = input.trim().length === 0;

        if (template && isEmpty) {
            deleteTemplate.mutate(template.key, {
                onSuccess: onSaveSuccess,
                onError: onSaveError,
            });
        } else if (template) {
            updateTemplate.mutate(
                { key: template.key, dto: { messageTemplate: input } },
                { onSuccess: onSaveSuccess, onError: onSaveError },
            );
        } else if (!isEmpty) {
            createTemplate.mutate(
                {
                    queueEntityKey: entityKey,
                    profileEntityKey: profileKey,
                    messageType: messageType,
                    messageTemplate: input,
                },
                { onSuccess: onSaveSuccess, onError: onSaveError },
            );
        } else {
            history.goBack();
        }
    };

    return (
        <Page
            header={`${i18next.t('tickets.autoreply.welcomeMessageTemplate')} — ${profile.get('description')} — ${i18next.t('tickets.autoreply.messageType.' + messageType)}`}
            onClickBack={() => history.goBack()}
        >
            <AutoReplyMessageForm
                key={`${profileKey}-${messageType}`}
                initialValue={template?.messageTemplate ?? ''}
                disabled={!queueEditable}
                onSave={handleSave}
            />
        </Page>
    );
};

export default AutoReplyMessage;
